import { store } from '../stateManager/store';
import { AlgoliaSDKService } from '../algoliaSDKservice/algoliaSDKservice';
import { SearchRequestParams } from '@msc-b2c/interfaces/types';
import {
  getCountryCode,
  getLanguageCode,
  getLocale,
  isPartnership,
} from '../utilFuncs/contextAwareUtils';
import { appendIsPackage, removeIsPackage } from './buildSelectedFilterQuery';
import { isBehindScenes, isDealsPage } from '../utilFuncs/dealsManagement';
import { getContextVariables } from '../genericUtils/algoliaContextUtils';
import { ComponentContext } from '../../../../interfaces/src/lib/enums/index';
import {
  buildFinalResponse,
  buildItineraryFiltersFromExcursionPorts,
  combineFilters,
  getPortsFilterFromItineraries,
  isDestinationFilterActive,
} from '../utilFuncs/utilFuncs';

export interface SearchResponse {
  nbHits: number;
  facets?: { [key: string]: { [key: string]: number } };
}

class Constants {
  public static readonly PRICE = 'price';
  public static readonly ORDER_DESC = 'desc';
  public static readonly EARLY_DATE = 'early';
  public static readonly DATE = 'date';
  public static readonly ORDER_ASC = 'asc';
  public static readonly RELEVANCE = 'relevance';
  public static readonly BEST_SAVING = 'best-saving';
  public static readonly ADULT = 'adult';
  public static readonly CABIN = 'cabin';
  public static readonly COUNTRY = 'gb';
  public static readonly LANGUAGE = 'en';
  public static readonly DEFAULT_LOCALE_PARTNERSHIP = 'en_AF';

  public static readonly ENVIRONMENT_NAME = Constants.getEnvironmentName() ?? 'perf';

  public static readonly PREFIX_INDEX_SEARCH = Constants.getPrefixIndexSearch() ?? 'perf';

  private static getEnvironmentName(): string | null {
    const envMetaTag = document.querySelector('meta[name="envType"]');
    return envMetaTag?.getAttribute('content') &&
      !(
        envMetaTag?.getAttribute('content') === 'test' ||
        envMetaTag?.getAttribute('content') === 'dev'
      )
      ? envMetaTag?.getAttribute('content')
      : 'perf';
  }

  private static getPrefixIndexSearch(): string | null {
    const envMetaTag = document.querySelector('meta[name="prefixIndexSearch"]');
    return envMetaTag?.getAttribute('content')
      ? envMetaTag?.getAttribute('content')
      : Constants.getEnvironmentName();
  }
}

const algoliaService = new AlgoliaSDKService({
  environment: Constants.ENVIRONMENT_NAME,
  prefixIndexSearch: Constants.PREFIX_INDEX_SEARCH,
});

function performSearch(
  algoliaSearchFunction: (params: {
    country: string;
    sortBy: string;
    language: string;
    searchParams: SearchRequestParams;
    order: string;
    priceType: string;
  }) => Promise<SearchResponse | { results: SearchResponse[] }>,
  searchParams: SearchRequestParams
): Promise<SearchResponse | { results: SearchResponse[] }> {
  // Get the sorting state from the store
  const sortingState = store.getState().sortingFilterBy;
  const { selectedSortingOptionFilterBy } = sortingState;
  const { selectedSortingOptionViewPrice } = store.getState().sortingViewPrice;
  // Get the sort order and sortBy from the selected sorting option
  const { order, sortBy, priceType } = getSortConfig(
    selectedSortingOptionFilterBy,
    selectedSortingOptionViewPrice
  );

  let country =
    getCountryCode()?.toLowerCase() ||
    document
      .querySelector('meta[name="countryCodeIso2"]')
      ?.getAttribute('content')
      ?.toLowerCase() ||
    Constants.COUNTRY;

  let language =
    getLanguageCode()?.toLowerCase() ||
    document.querySelector('meta[name="languageCode"]')?.getAttribute('content')?.toLowerCase() ||
    Constants.LANGUAGE;

  if (isPartnership()) {
    const locale =
      getLocale() ||
      document.querySelector('meta[name="localeCode"]')?.getAttribute('content')?.toLowerCase() ||
      Constants.DEFAULT_LOCALE_PARTNERSHIP;
    [language, country] = locale.toLowerCase().split('_');
  }

  // Call the provided search function with the search parameters and sort options
  return algoliaSearchFunction({
    searchParams: searchParams,
    sortBy: sortBy,
    order: order,
    country: country,
    language: language,
    priceType: priceType,
  });
}

function getSortConfig(
  sortOrder: string | null,
  priceTypeSelection = ''
): {
  order: string;
  sortBy: string;
  priceType: string;
} {
  // Check if sortOrder is null or empty and set default values if true
  if (!sortOrder) {
    sortOrder = Constants.ORDER_ASC;
  }

  // Determine the order based on the sortOrder value
  // if the sortOrder is relevance then order should be empty
  // if instead the sortOrder is DESC then order should be desc otherwise asc
  const order =
    sortOrder === Constants.RELEVANCE || sortOrder === Constants.BEST_SAVING
      ? ''
      : sortOrder === Constants.ORDER_DESC
      ? Constants.ORDER_DESC
      : Constants.ORDER_ASC;

  // Map the sorting options to the Algolia sorting options
  const sortOptions: { [key: string]: string } = {
    [Constants.RELEVANCE]: Constants.RELEVANCE,
    [Constants.EARLY_DATE]: Constants.DATE,
    [Constants.ORDER_DESC]: Constants.PRICE,
    [Constants.ORDER_ASC]: Constants.PRICE,
    [Constants.BEST_SAVING]: Constants.BEST_SAVING,
  };

  const priceTypeOption: { [key: string]: string } = {
    person: Constants.ADULT,
    [Constants.CABIN]: Constants.CABIN,
  };

  // Default to price if the sort order is not recognized
  const sortBy = sortOptions[sortOrder] || '';

  const priceType = priceTypeOption[priceTypeSelection] || '';

  return { order, sortBy, priceType };
}

export const searchService = {
  // Perform search for itineraries
  performSearchItineraries: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getItinerariesFromAlgoliaV4(
          params.searchParams,
          params.sortBy,
          params.order,
          params.country,
          params.language,
          params.priceType
        ),
      searchParams
    );
  },

  // Perform search for cruises
  performSearchCruises: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getCruisesFromAlgoliaV1(
          params.searchParams,
          params.country,
          params.language
        ),
      searchParams
    );
  },

  // Perform search for cruises
  performSearchCruisesAir: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getCruisesFromAlgoliaV1Air(
          params.searchParams,
          params.country,
          params.language
        ),
      searchParams
    );
  },

  // Perform search for datesLayer
  // TODO to be removed after the TL rework is done
  performSearchCabinsAndDatesLayer: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getItinerariesFromAlgoliaV4(
          params.searchParams,
          params.sortBy,
          params.order,
          params.country,
          params.language,
          params.priceType
        ),
      searchParams
    );
  },

  performSearchCabinsAndDatesLayerAir: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getItinerariesFromAlgoliaV4Air(
          params.searchParams,
          params.sortBy,
          params.order,
          params.country,
          params.language,
          params.priceType
        ),
      searchParams
    );
  },

  // Perform search for cruises
  performSearchMultipleQueries: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getMultipleItinerariesFromAlgoliaV1(
          params.searchParams,
          params.sortBy,
          params.order,
          params.country,
          params.language,
          params.priceType
        ),
      searchParams
    );
  },

  performSearchMultipleQueriesAir: (searchParams: SearchRequestParams) => {
    return performSearch(
      (params) =>
        algoliaService.getMultipleItinerariesFromAlgoliaV1Air(
          params.searchParams,
          params.sortBy,
          params.order,
          params.country,
          params.language,
          params.priceType
        ),
      searchParams
    );
  },

  // Perform search for excursions

  performSearchExcursions: async (searchParams: SearchRequestParams) => {
    let mainFilters = '';
    let destinationFilter = '';
    const { dealsConfig } = store.getState();

    const packageExplorer = dealsConfig?.dealsFilters?.some(
      (item) => item.filterType === 'category' && item.filterValue === 'PCKCRU'
    );

    const filters = searchParams.filters;
    const hasCategoryCdFilter = filters.indexOf('categoryCd') > -1;

    // Split filters into main and destination
    if (filters.includes('|')) {
      const [main, destination] = filters.split('|');
      mainFilters = appendIsPackage(main.trim());
      destinationFilter = destination
        .trim()
        .replace(/destination/g, 'commArea.key')
        .replace('AND isPackage:false', '');
    } else if (filters.toLowerCase().includes('destination')) {
      destinationFilter = filters
        .trim()
        .replace(/destination/g, 'commArea.key')
        .replace('AND isPackage:false', '');
    } else {
      mainFilters = appendIsPackage(filters.trim());
    }
    mainFilters = appendIsPackage(mainFilters);

    if (packageExplorer) {
      mainFilters = removeIsPackage(mainFilters);
    }

    // Extract portCodes from mainFilters
    const portCodeMatches = mainFilters.match(/portCode:"([^"]+)"/g) || [];
    const portCodes = portCodeMatches.map((match) => match.match(/"([^"]+)"/)?.[1]).filter(Boolean);

    const destinationFilterIsActive = isDestinationFilterActive();

    let itineraryResults: SearchResponse = { nbHits: 0, facets: {} };
    let excursionResults: SearchResponse = { nbHits: 0, facets: {} };

    // 🔹 Always perform itinerary search if destination filter is active
    if (destinationFilterIsActive) {
      const itineraryFilterParts: string[] = [removeIsPackage(destinationFilter)];

      if (portCodes.length > 0) {
        const portOfCallsFilter = portCodes.map((code) => `portOfCalls:"${code}"`).join(' OR ');
        const embkPortFilter = portCodes.map((code) => `embkPort.key:"${code}"`).join(' OR ');
        itineraryFilterParts.push(`(${portOfCallsFilter} OR ${embkPortFilter})`);
      }

      const itinerarySearchParams = {
        ...searchParams,
        context: getContextVariables(ComponentContext.FycFilters, isDealsPage()),
        filters:
          itineraryFilterParts[0] === ''
            ? itineraryFilterParts.join('')
            : itineraryFilterParts.join(' AND '),
      };

      const itinerarySearchResponse = await performSearch(
        (params) =>
          algoliaService.getItinerariesFromAlgoliaV4(
            params.searchParams,
            params.sortBy,
            params.order,
            params.country,
            params.language,
            params.priceType
          ),
        itinerarySearchParams
      );

      itineraryResults =
        'results' in itinerarySearchResponse
          ? itinerarySearchResponse.results[0]
          : itinerarySearchResponse;
    }

    // 🔹 Always generate portsFilter from itineraryResults
    const portsFilter = getPortsFilterFromItineraries(itineraryResults, destinationFilterIsActive);

    // 🔹 Always combine main filters with portsFilter for the excursions search
    let finalFilters = combineFilters(mainFilters, portsFilter);
    if (isBehindScenes()) {
      finalFilters = '(categoryCd:"BEHINDTHESCENES")';
    }
    const excursionSearchParams = {
      ...searchParams,
      filters: finalFilters,
      context: getContextVariables(
        isDealsPage() ? ComponentContext.DealsPage : ComponentContext.ResultsPage,
        isDealsPage()
      ),
    };

    const excursionRawResults = await performSearch(
      (params) =>
        algoliaService.getExcursionsFromAlgoliaV1(
          params.searchParams,
          params.country,
          params.language
        ),
      excursionSearchParams
    );

    excursionResults =
      'results' in excursionRawResults ? excursionRawResults.results[0] : excursionRawResults;

    // 🔹 If categoryCd is present and we have portCodes from excursions, perform a second itinerary search
    if (
      hasCategoryCdFilter &&
      excursionResults.facets?.['portCode'] &&
      Object.keys(excursionResults.facets['portCode']).length > 0
    ) {
      const itineraryFilters = buildItineraryFiltersFromExcursionPorts(
        excursionResults.facets['portCode']
      );

      const itinerarySearchParams = {
        ...searchParams,
        filters: itineraryFilters,
        context: getContextVariables(ComponentContext.FycFilters, isDealsPage()),
      };

      const itineraryRawResults = await performSearch(
        (params) =>
          algoliaService.getItinerariesFromAlgoliaV4(
            params.searchParams,
            params.sortBy,
            params.order,
            params.country,
            params.language,
            params.priceType
          ),
        itinerarySearchParams
      );

      const secondItineraryResults =
        'results' in itineraryRawResults ? itineraryRawResults.results[0] : itineraryRawResults;

      // 🔹 Return final merged response
      return buildFinalResponse(excursionResults, itineraryResults, secondItineraryResults);
    }

    // 🔹 Return response without second itinerary call
    return buildFinalResponse(excursionResults, itineraryResults);
  },
};
